import { type ZodSchema } from "zod";
import type z from "zod";

import { UnauthorizedError } from "../http";
import { API_URLS, type Env } from "../http/Env";
import { safeStringify } from "../json";

export interface GraphQlRequestOptions<TQuery, TResponseSchema> {
	env: Env;
	fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
	authorizationHeader: string | undefined;
	query: TQuery;
	responseSchema: TResponseSchema;
	additionalHeaders?: Record<string, string>;
}

export class GQLError extends Error {
	readonly extras: unknown;

	constructor(message: string, extras: unknown) {
		super(message);
		this.extras = extras;
	}
}

export async function graphqlRequest<TQuery, TResponseSchema extends ZodSchema>({
	env,
	fetch,
	authorizationHeader,
	query,
	responseSchema,
	additionalHeaders,
}: GraphQlRequestOptions<TQuery, TResponseSchema>): Promise<z.infer<TResponseSchema>> {
	if (!authorizationHeader) {
		throw new Error("authorizationHeader is required");
	}
	const headers = {
		"Content-Type": "application/json",
		"Authorization": authorizationHeader,
		"Origin": "cloudflare",
	};
	if (additionalHeaders) {
		for (const [key, value] of Object.entries(additionalHeaders)) {
			headers[key] = value;
		}
	}

	const response = await fetch(`${API_URLS[env]}/query`, {
		method: "POST",
		headers,
		body: safeStringify(query),
	});
	if (response.status === 401) {
		const text = await response.text();
		throw new UnauthorizedError(`${response.status}: ${text}`);
	} else if (!response.ok) {
		// FIXME: Enumerate more response codes and provide better errors?
		const text = await response.text();
		throw new Error(`${response.status}: ${text}`);
	}

	const json = await response.json();
	if (!("data" in json)) {
		throw new Error(`Valid GraphQL responses have a 'data' field, got: ${safeStringify(json)}`);
	} else if ("errors" in json) {
		throw new GQLError(json["errors"] ? json["errors"] : "error present in gql response", {
			responseData: safeStringify(json, 2),
			operationName: query["operationName"],
		});
	}
	try {
		return responseSchema.parse(json.data);
	} catch (e) {
		throw new GQLError("unable to parse response data", {
			originalError: e,
			responseData: safeStringify(json, 2),
			operationName: query["operationName"],
		});
	}
}
